import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { Alert, AlertTitle, Stack, TextField, Typography } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import ModalTemplate from 'v2/components/Modal/ModalTemplate';
import useModalContext from 'v2/components/Modal/useModalContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';

interface FormValues {
  description: string;
}

interface Props {
  cardUuid: string;
  onConfirm: (uuid: string, reason: string) => Promise<void>;
}

const FORM_ID = 'block-access-card-form';

const BlockAccessCardModal: React.FC<Props> = ({ cardUuid, onConfirm }) => {
  const { translation } = useTranslation();
  const { closeModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: { description: '' }
  });

  const onSubmit = handleSubmit(async ({ description }) => {
    setIsSubmitting(true);
    try {
      await onConfirm(cardUuid, description);
      showSnackbar(translation.access_card_blocked, { severity: 'success' });
      closeModal();
    } catch {
      setIsSubmitting(false);
    }
  });

  return (
    <ModalTemplate
      title={translation.block}
      primaryAction={{
        label: translation.block,
        form: FORM_ID,
        dangerzone: true,
        loading: isSubmitting,
        id: 'block-access-card-confirm-button'
      }}
      secondaryAction={{ label: translation.cancel, id: 'block-access-card-cancel-button' }}
    >
      <Stack component="form" id={FORM_ID} onSubmit={onSubmit} noValidate gap={3}>
        <Typography variant="body2">{translation.block_card_confirmation}</Typography>
        <TextField
          {...register('description')}
          id="description-input"
          label={translation.description}
          placeholder={translation.block_card_description_placeholder}
          multiline
          minRows={2}
          fullWidth
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Alert severity="warning" icon={<WarningAmberOutlined />}>
          <AlertTitle>{translation.access_card_still_functional}</AlertTitle>
          {translation.block_card_sync_notice}
        </Alert>
      </Stack>
    </ModalTemplate>
  );
};

export default BlockAccessCardModal;
